import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft, ExternalLink, RefreshCw, Edit3, Star, AlertCircle, Image as ImageIcon
} from 'lucide-react';
import { getModel } from '../api/models';
import ModelImageUploader from '../components/ModelImageUploader';
import CreatorPageSettings from '../components/CreatorPageSettings';
import { pickImageUrl } from '../utils/imageUrls';
import PageHeader from '../components/shared/PageHeader';

type TabKey = 'overview' | 'images' | 'tags' | 'creator-page' | 'pins' | 'activity' | 'audit';

const TAB_LABELS: Record<TabKey, string> = {
  overview: 'Overview', images: 'Images', tags: 'Tags',
  'creator-page': 'Creator page', pins: 'Pins', activity: 'Activity', audit: 'Audit',
};

const fmt = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n)
    ? new Intl.NumberFormat('en-US').format(n)
    : '—';

const ModelDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const modelId = Number(id);
  const [tab, setTab] = useState<TabKey>('overview');

  const { data: model, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['model', modelId],
    queryFn: () => getModel(modelId),
    enabled: Number.isFinite(modelId)
  });

  if (!Number.isFinite(modelId)) {
    return (
      <>
        <PageHeader title="Model not found" />
        <div className="hf-content">
          <div className="hf-banner danger"><AlertCircle size={14} /> Invalid model ID</div>
          <button className="hf-btn" onClick={() => navigate('/models')}><ArrowLeft size={14} /> Back to Models</button>
        </div>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <PageHeader title="Model" />
        <div className="hf-content">
          <div className="hf-card"><div className="hf-card-body" style={{ textAlign: 'center', color: 'var(--fg-3)' }}>Loading…</div></div>
        </div>
      </>
    );
  }

  if (error || !model) {
    return (
      <>
        <PageHeader title="Model" />
        <div className="hf-content">
          <div className="hf-banner danger"><AlertCircle size={14} /> Failed to load model details.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="hf-btn" onClick={() => refetch()}><RefreshCw size={14} /> Retry</button>
            <button className="hf-btn" onClick={() => navigate('/models')}><ArrowLeft size={14} /> Back</button>
          </div>
        </div>
      </>
    );
  }

  const displayName = model.modelName || model.modelUsername;
  const initial = displayName.charAt(0).toUpperCase();
  const profileImage = pickImageUrl(model.imageUrls, model.imageFilename);

  const title = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
      <button className="hf-btn icon ghost" onClick={() => navigate(-1)} aria-label="Back">
        <ArrowLeft size={16} />
      </button>
      <span className="hf-avatar lg">
        {profileImage ? <img src={profileImage} alt={displayName} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} /> : initial}
      </span>
      <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>{displayName}</span>
      <span className="hf-mono" style={{ color: 'var(--fg-3)', fontSize: 14 }}>#{model.id}</span>
      {model.promote && <span className="hf-tag accent">promoted</span>}
      {model.blacklisted && <span className="hf-tag danger">blacklisted</span>}
      {model.dmcaBlocked && <span className="hf-tag warning">DMCA</span>}
      {model.isPromo && <span className="hf-tag outline">isPromo</span>}
    </span>
  );

  const subtitle = (
    <>
      <span className="hf-mono">@{model.modelUsername}</span>
      {model.source && <> · {model.source}</>}
      {model.updatedAt && <> · Updated {new Date(model.updatedAt).toLocaleString()}</>}
    </>
  );

  const actions = (
    <>
      {model.onlyfansUrl && (
        <a className="hf-btn" href={model.onlyfansUrl} target="_blank" rel="noreferrer noopener">
          <ExternalLink size={14} /> Open public page
        </a>
      )}
      <button className="hf-btn" onClick={() => refetch()} disabled={isFetching}>
        <RefreshCw size={14} /> {isFetching ? 'Refetching…' : 'Re-fetch'}
      </button>
      <Link className="hf-btn primary" to={`/models/${model.id}/edit`}>
        <Edit3 size={14} /> Edit
      </Link>
    </>
  );

  return (
    <>
      <PageHeader title={title} subtitle={subtitle} actions={actions} />
      <div className="hf-content">

        {/* Score strip — 5 KPIs */}
        <div className="hf-kpi-strip">
          <div className="hf-kpi accent">
            <span className="hf-kpi-label">Final score</span>
            <span className="hf-kpi-value">{model.finalScore?.toFixed(1) ?? '—'}</span>
            <span className="hf-kpi-sub">0–10 scale</span>
          </div>
          <div className="hf-kpi">
            <span className="hf-kpi-label">Likes</span>
            <span className="hf-kpi-value">{fmt(model.likesCount)}</span>
          </div>
          <div className="hf-kpi">
            <span className="hf-kpi-label">PPC order</span>
            <span className="hf-kpi-value">{model.ppcOrder ?? '—'}</span>
            <span className="hf-kpi-sub">{model.ppcType ?? '—'}</span>
          </div>
          <div className="hf-kpi">
            <span className="hf-kpi-label">Monetisation</span>
            <span className="hf-kpi-value" style={{ fontSize: 18 }}>{model.monetizationGroup ?? '—'}</span>
            <span className="hf-kpi-sub">{model.category ?? ''}</span>
          </div>
          <div className="hf-kpi">
            <span className="hf-kpi-label">Manual bias</span>
            <span className="hf-kpi-value">{model.manualBias ?? '—'}</span>
            <span className="hf-kpi-sub">priority: {model.priority ?? '—'}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="hf-tabstrip">
          {(['overview', 'images', 'tags', 'creator-page', 'pins', 'activity', 'audit'] as TabKey[]).map(t => (
            <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
              {TAB_LABELS[t]}
              {t === 'images' && <span className="ct">{Object.keys(model.imageUrls ?? {}).length}</span>}
              {t === 'tags' && <span className="ct">{model.tags?.length ?? 0}</span>}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="hf-three-col">
            {/* Left column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="hf-card">
                <div className="hf-card-header"><h3 className="hf-card-title">Profile</h3></div>
                <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <KV k="Model ID"  v={<span className="hf-mono">#{model.id}</span>} />
                  <KV k="Username"  v={<span className="hf-mono">@{model.modelUsername}</span>} />
                  <KV k="Source"    v={model.source ?? '—'} />
                  <KV k="Category"  v={model.category ?? '—'} />
                  <KV k="Price"     v={model.price ?? '—'} />
                  <KV k="Final"     v={<span className="hf-mono">{model.finalScore?.toFixed(1) ?? '—'}</span>} />
                </div>
              </div>

              <div className="hf-card">
                <div className="hf-card-header"><h3 className="hf-card-title">Flags</h3></div>
                <div className="hf-card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {model.promote && <span className="hf-tag accent">promote</span>}
                  {model.blacklisted && <span className="hf-tag danger">blacklisted</span>}
                  {model.dmcaBlocked && <span className="hf-tag warning">DMCA</span>}
                  {model.isPromo && <span className="hf-tag outline">isPromo</span>}
                  {model.creatorPage && <span className="hf-tag success">creator page</span>}
                  {model.manualImage && <span className="hf-tag">manual image</span>}
                  {model.manualDescription && <span className="hf-tag">manual desc</span>}
                  {model.manualPrice && <span className="hf-tag">manual price</span>}
                  {![model.promote, model.blacklisted, model.dmcaBlocked, model.isPromo, model.creatorPage].some(Boolean) && (
                    <span className="hf-help" style={{ marginTop: 0 }}>No flags set.</span>
                  )}
                </div>
              </div>

              <div className="hf-card">
                <div className="hf-card-header">
                  <h3 className="hf-card-title">Tags</h3>
                  <span className="hf-count">{model.tags?.length ?? 0}</span>
                </div>
                <div className="hf-card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {model.tags && model.tags.length > 0
                    ? model.tags.map((t) => <span key={t} className="hf-tag">{t}</span>)
                    : <span className="hf-help" style={{ marginTop: 0 }}>No tags assigned.</span>}
                </div>
              </div>
            </div>

            {/* Right column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="hf-card">
                <div className="hf-card-header">
                  <h3 className="hf-card-title">Cover</h3>
                  <Star size={13} style={{ color: model.promote ? 'var(--accent)' : 'var(--fg-3)' }} />
                </div>
                <div className="hf-card-body">
                  <ModelImageUploader model={model} onImageUpdate={() => refetch()} />
                </div>
              </div>

              {model.description && (
                <div className="hf-card">
                  <div className="hf-card-header"><h3 className="hf-card-title">Description</h3></div>
                  <div className="hf-card-body" style={{ color: 'var(--fg-2)' }}>{model.description}</div>
                </div>
              )}

              <div className="hf-card">
                <div className="hf-card-header">
                  <h3 className="hf-card-title">Clicks · 30 day</h3>
                  <span className="hf-help" style={{ marginTop: 0 }}>chart placeholder — wire to /analytics</span>
                </div>
                <div className="hf-card-body" style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fg-4)' }}>
                  <ImageIcon size={28} />
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 'images' && (
          <div className="hf-card">
            <div className="hf-card-header"><h3 className="hf-card-title">Images</h3></div>
            <div className="hf-card-body">
              <ModelImageUploader model={model} onImageUpdate={() => refetch()} />
            </div>
          </div>
        )}

        {tab === 'tags' && (
          <div className="hf-card">
            <div className="hf-card-header"><h3 className="hf-card-title">Tags</h3></div>
            <div className="hf-card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {model.tags && model.tags.length > 0
                ? model.tags.map((t) => <span key={t} className="hf-tag">{t}</span>)
                : <span className="hf-help" style={{ marginTop: 0 }}>No tags assigned.</span>}
              <Link to={`/models/${model.id}/edit`} className="hf-btn sm" style={{ marginLeft: 'auto' }}>
                <Edit3 size={12} /> Manage tags
              </Link>
            </div>
          </div>
        )}

        {tab === 'creator-page' && <CreatorPageSettings model={model} />}

        {(tab === 'pins' || tab === 'activity' || tab === 'audit') && (
          <div className="hf-card">
            <div className="hf-card-body" style={{ textAlign: 'center', color: 'var(--fg-3)' }}>
              {tab === 'pins' && 'Pin overrides for this model — coming soon.'}
              {tab === 'activity' && 'Activity log — coming soon.'}
              {tab === 'audit' && 'Audit trail — coming soon.'}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const KV: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, gap: 12 }}>
    <span style={{ color: 'var(--fg-3)' }}>{k}</span>
    <span style={{ color: 'var(--fg-1)', textAlign: 'right' }}>{v}</span>
  </div>
);

export default ModelDetailPage;
